import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import PackageCard from '../components/PackageCard';
import SEO from '../components/SEO';
import { toUploadUrl } from '../config/api';
import { packageAPI, reviewAPI } from '../services/api';

const Packages = () => {
  const location = useLocation();
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [searchTerm, setSearchTerm] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    return toUploadUrl(imagePath, {
      fallback: 'https://placehold.co/800x600/1a1a1a/ffffff?text=No+Image'
    });
  };

  // Parse URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    const trendingParam = params.get('trending');
    const destinationParam = params.get('destination');
    const searchParam = params.get('search');

    if (typeParam) setFilter(typeParam);
    if (searchParam) setSearchTerm(searchParam);
    if (destinationParam) setSearchTerm(destinationParam);
    if (trendingParam === 'true') setFilter('trending');
  }, [location]);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await packageAPI.getAllPackages();
      const packageList = getResponseArray(response);
      const packageIds = packageList.map((pkg) => pkg.id).filter(Boolean);
      let reviewSummaryMap = {};

      if (packageIds.length) {
        try {
          const reviewSummaryRes = await reviewAPI.getSummary('package', packageIds);
          reviewSummaryMap = (reviewSummaryRes.data?.data || []).reduce((acc, item) => {
            acc[item.entity_id] = item;
            return acc;
          }, {});
        } catch (reviewError) {
          console.error('Error fetching package review summary:', reviewError);
        }
      }
      
      // Process packages to ensure image paths are correct
      const processedPackages = packageList.map(pkg => ({
        ...pkg,
        image: getImageUrl(pkg.image),
        gallery: pkg.gallery?.map(img => getImageUrl(img)),
        average_rating: reviewSummaryMap[pkg.id]?.average_rating || pkg.average_rating || pkg.rating || 0,
        review_count: reviewSummaryMap[pkg.id]?.review_count || pkg.review_count || pkg.reviews || 0
      }));
      
      setPackages(processedPackages);
      setError(null);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Failed to load packages. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Filter packages based on selected filter
  const filteredPackages = packages.filter(pkg => {
    if (filter === 'all') return true;
    if (filter === 'trending') return pkg.trending === true || pkg.trending === 1;
    if (filter === 'discount') return pkg.discount && parseFloat(pkg.discount) > 0;
    if (filter === 'group' || filter === 'corporate') {
      return String(pkg.package_category || 'general').toLowerCase() === filter;
    }
    return pkg.type === filter;
  });

  // Sort packages based on selected sort
  const sortedPackages = [...filteredPackages].sort((a, b) => {
    if (sortBy === 'price-low') return (a.price || 0) - (b.price || 0);
    if (sortBy === 'price-high') return (b.price || 0) - (a.price || 0);
    if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
    if (sortBy === 'popular') return (b.reviews || 0) - (a.reviews || 0);
    return 0;
  });

  // Filter by search term
  const displayedPackages = sortedPackages.filter(pkg =>
    pkg.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.destinations?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const packageTypes = [
    { id: 'all', label: 'All Packages', icon: 'fas fa-box' },
    { id: 'trending', label: 'Trending', icon: 'fas fa-fire' },
    { id: 'featured', label: 'Featured', icon: 'fas fa-star' },
    { id: 'group', label: 'Group', icon: 'fas fa-users' },
    { id: 'corporate', label: 'Corporate', icon: 'fas fa-briefcase' },
    { id: 'honeymoon', label: 'Honeymoon', icon: 'fas fa-heart' },
    { id: 'family', label: 'Family', icon: 'fas fa-users' },
    { id: 'adventure', label: 'Adventure', icon: 'fas fa-hiking' },
    { id: 'luxury', label: 'Luxury', icon: 'fas fa-gem' },
    { id: 'budget', label: 'Budget', icon: 'fas fa-wallet' },
    { id: 'nature', label: 'Nature', icon: 'fas fa-tree' },
    { id: 'spiritual', label: 'Spiritual', icon: 'fas fa-pray' }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading amazing packages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <i className="fas fa-exclamation-circle"></i>
        <h3>Oops! Something went wrong</h3>
        <p>{error}</p>
        <button onClick={fetchPackages} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="packages-page">
      <SEO
        title="Tour Packages | RedGoTrip Holidays"
        description="Browse RedGoTrip tour packages for family trips, honeymoon holidays, adventure escapes and budget travel across top destinations."
        canonicalPath={`/packages${location.search || ''}`}
        keywords="tour packages, holiday packages, honeymoon packages, family tours, budget trips, RedGoTrip packages"
      />
      {/* Page Header */}
      <div className="packages-header">
        <div className="header-content">
          <h1>Holidays Tour Packages</h1>
          <p>Discover the perfect India tour package for an unforgettable experience</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="packages-search">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search packages by name or destination..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      {/* Filter Section */}
      <div className="packages-filter">
        <div className="filter-wrapper">
          <div className="filter-types">
            {packageTypes.map(type => (
              <button
                key={type.id}
                className={`filter-btn ${filter === type.id ? 'active' : ''}`}
                onClick={() => setFilter(type.id)}
              >
                <i className={type.icon}></i>
                <span>{type.label}</span>
              </button>
            ))}
          </div>
          
          <div className="sort-section">
            <label htmlFor="sort">Sort by:</label>
            <select 
              id="sort" 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="popular">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <p>
          <strong>{displayedPackages.length}</strong> packages found
          {filter !== 'all' && ` in ${filter} category`}
          {searchTerm && ` matching "${searchTerm}"`}
        </p>
      </div>

      {/* Packages Grid */}
      <div className="packages-grid-container">
        {displayedPackages.length > 0 ? (
          <div className="packages-grid">
            {displayedPackages.map(pkg => (
              <PackageCard key={pkg.id} package={pkg} />
            ))}
          </div>
        ) : (
          <div className="no-results">
            <i className="fas fa-search"></i>
            <h3>No packages found</h3>
            <p>Try adjusting your filters or search term</p>
            <button 
              className="reset-btn"
              onClick={() => {
                setFilter('all');
                setSearchTerm('');
                setSortBy('popular');
              }}
            >
              Reset All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function getResponseArray(response) {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.packages)) return payload.packages;

  return [];
}

export default Packages;
